import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';

import { filesAPIService } from '~/api/files';
import ImageUploader from '~/components/ui/ImageUploader/ImageUploader';
import ImageUploaderModal from '~/components/ui/ImageUploaderModal/ImageUploaderModal';
import Select from '~/components/ui/Select';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { StyledNamesContainer } from '~/components/VCardPlusQRContent/VCardPlusBasicInformation/VCardPlusAboutYou/styled';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { VCardPlusData } from '~/types/qrCode';
import { isMaxLength } from '~/utils/common';
import { logger } from '~/utils/logger';

type VCardPlusAboutYouProps = {
  updateVCardPlusPersonData?: () => void;
  updateVCardPlusData?: () => void;
  vCardPlusData: VCardPlusData;
  isBuilder?: boolean;
  showCoverImage?: boolean;
};

export default function VCardPlusAboutYou({
  updateVCardPlusPersonData,
  vCardPlusData,
  updateVCardPlusData,
  isBuilder,
  showCoverImage,
}: VCardPlusAboutYouProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const [uploaderModal, setUploaderModal] = useState(false);
  const [name, setName] = useState(vCardPlusData?.person?.firstName || '');
  const [surname, setSurname] = useState(vCardPlusData?.person?.lastName || '');
  const [title, setTitle] = useState(vCardPlusData?.person?.title || '');
  const [titlePosition, setTitlePosition] = useState(vCardPlusData?.personTitle || '');
  const [avatar, setAvatar] = useState(vCardPlusData?.person?.photo?.logo || '');
  const [coverImage, setCoverImage] = useState(vCardPlusData?.coverImage || '');

  const vCardNames = [
    {
      label: t('other:name'),
      value: name,
      placeholder: t('common:enterName'),
      id: 'name',
      idBE: 'first_name',
      isRequired: true,
      maxLength: 70,
      onChange: setName,
      onBlur: () => {
        updateVCardPlusPersonData({ first_name: name });
      },
    },
    {
      label: t('other:surname'),
      placeholder: t('common:enterSurname'),
      value: surname,
      id: 'surName',
      idBE: 'last_name',
      maxLength: 70,
      onChange: setSurname,
      onBlur: () => {
        updateVCardPlusPersonData({ last_name: surname });
      },
    },
  ];

  const titlePositionItems = [
    { value: 'LEFT', label: t('template5:titlePosition.left') },
    { value: 'RIGHT', label: t('template5:titlePosition.right') },
    { value: 'CENTERED', label: t('template5:titlePosition.center') },
  ];

  const addAvatar = async ({ file, rotate, zoom, roundness }) => {
    if (file?.file) {
      setAvatar(file?.previewLink);

      try {
        const storageResponse = await filesAPIService.uploadImage(file.file[0]);

        if (storageResponse?.data) {
          await updateVCardPlusPersonData({
            photo: {
              border_radius: roundness,
              logo_id: storageResponse.data,
              rotate,
              scale: zoom,
            },
          });

          setUploaderModal(false);
        }
      } catch (e) {
        logger.error('updateVCardPlusPersonData', e);
      }
    } else {
      try {
        await updateVCardPlusPersonData({
          photo: {
            border_radius: roundness,
            rotate,
            scale: zoom,
          },
        });
        setUploaderModal(false);
      } catch (e) {
        logger.error('updateVCardPlusPersonData', e);
      }
    }
  };

  const removeAvatar = () => {
    setAvatar('');
    void updateVCardPlusPersonData({
      photo: {
        border_radius: 50,
        logo_id: null,
        rotate: 0,
        scale: 100,
      },
    });
  };

  const addCoverImage = async (event) => {
    const file = event.target.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCoverImage(reader.result);
      };
      reader.readAsDataURL(file);
      try {
        const storageResponse = await filesAPIService.uploadImage(file);

        if (storageResponse?.data) {
          await updateVCardPlusData({
            cover_image_id: storageResponse.data,
          });
        }
      } catch (e) {
        logger.error('addCoverImage', e);
      }
    }
  };

  const removeCoverImage = () => {
    setCoverImage('');
    void updateVCardPlusData({
      cover_image_id: null,
    });
  };

  return (
    <>
      {isBuilder && (
        <Text variant="inputSmall" sx={{ marginBottom: '8px' }}>
          {t('template5:yourAvatar')}
        </Text>
      )}
      <ImageUploader
        image={avatar}
        onDeleteImage={removeAvatar}
        withUploaderModal
        onOpenUploaderModal={() => setUploaderModal(true)}
      />
      {showCoverImage && (
        <>
          <Text variant="inputSmall" sx={{ marginBottom: '8px', marginTop: '12px' }}>
            {t('template5:coverImage')}
          </Text>
          <ImageUploader image={coverImage} onDeleteImage={removeCoverImage} onUploadImage={addCoverImage} />
        </>
      )}
      {uploaderModal && (
        <ImageUploaderModal
          image={avatar}
          open={uploaderModal}
          setOpen={setUploaderModal}
          onSave={addAvatar}
          defaultRotate={vCardPlusData?.person?.photo?.rotate}
          defaultRoundness={vCardPlusData?.person?.photo?.borderRadius}
          defaultZoom={vCardPlusData?.person?.photo?.scale}
        />
      )}
      <StyledNamesContainer>
        {vCardNames.map((item) => {
          return (
            <TextInput
              key={item.id}
              value={item.value}
              label={item.label}
              placeholder={item.placeholder}
              fullWidth
              error={!item.value && item.isRequired}
              inputProps={{ maxLength: item.maxLength }}
              helperText={
                (item?.isRequired && !item.value && t('inputs:required')) || isMaxLength(item.value, item.maxLength)
              }
              onChange={(e) => {
                item.onChange(e.target.value);
              }}
              onBlur={item.onBlur}
            />
          );
        })}
      </StyledNamesContainer>
      <TextInput
        value={title}
        label={t('other:title')}
        placeholder={t('common:enterName')}
        fullWidth
        error={!title}
        inputProps={{ maxLength: 100 }}
        helperText={(!title && t('inputs:required')) || isMaxLength(title, 100)}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => updateVCardPlusPersonData({ title })}
        labelSx={{ marginTop: '12px' }}
      />
      <Select
        value={titlePosition}
        items={titlePositionItems}
        labelSx={{ marginTop: '12px' }}
        label={t('other:titlePosition')}
        sx={{ width: isDesktop ? 'calc(50% - 16px)' : '100%' }}
        onChange={(e) => setTitlePosition(e.target.value)}
        onBlur={() => {
          void updateVCardPlusData({ person_title: titlePosition });
        }}
      />
    </>
  );
}
